import * as React from "react";
import gql from "graphql-tag";
import { ChildProps, graphql } from "react-apollo";
import { RouteComponentProps, withRouter } from "react-router";
import { MenuItem, TextField } from "@material-ui/core";
import BOGraphQLLoader from "app/components/BOComponents/BOGraphQLLoader";
import ConfigurableTable from "app/components/AppComponents/ConfigurableTable";
import withPagination, { PaginationProps } from "app/utils/withPagination";
import buildTicketEventColumns, {
  getDisplayStatus,
} from "./ticketEventColumns";
import {
  TicketEvent,
  REVIEWABLE_STATUSES,
  ALL_TICKET_EVENT_STATUSES,
} from "./ticketEventTypes";

type GqlData = {
  ticket_events: Array<TicketEvent>;
};

type Props = ChildProps<
  {
    name: string;
    mode: "all" | "review_queue";
    emptyStateMessage: string;
  } & RouteComponentProps &
    PaginationProps,
  GqlData
>;

type State = {
  search: string;
  statusFilter: string;
};

class TicketEventsTableContainer extends React.Component<Props, State> {
  state = {
    search: "",
    statusFilter: "all",
  };

  onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ search: event.target.value });
  };

  onStatusFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ statusFilter: event.target.value });
  };

  getBaseEvents() {
    const ticketEvents = this.props.data.ticket_events || [];

    if (this.props.mode === "review_queue") {
      return ticketEvents.filter((ticketEvent) =>
        REVIEWABLE_STATUSES.includes(ticketEvent.status)
      );
    }

    return ticketEvents;
  }

  getStatusOptions() {
    if (this.props.mode === "review_queue") {
      return [
        { label: "All review queue statuses", value: "all" },
        { label: "Pending approval", value: "pending_approval" },
        { label: "Changes requested", value: "changes_requested" },
      ];
    }

    return [
      { label: "All statuses", value: "all" },
      ...ALL_TICKET_EVENT_STATUSES.map((status) => ({
        label: status.replace(/_/g, " "),
        value: status,
      })),
    ];
  }

  getFilteredEvents() {
    const baseEvents = this.getBaseEvents();
    const searchTerm = this.state.search.trim().toLowerCase();

    return baseEvents.filter((ticketEvent) => {
      const displayStatus = getDisplayStatus(
        ticketEvent.status,
        ticketEvent.end_date
      );

      const matchesStatus =
        this.state.statusFilter === "all" ||
        displayStatus === this.state.statusFilter;

      const searchableText = [
        ticketEvent.name,
        ticketEvent.code,
        ticketEvent.external_reference,
        ticketEvent.description,
        ticketEvent.facebook_page,
        ticketEvent.instagram_page,
        ticketEvent.approval_comments,
        ticketEvent.merchant && ticketEvent.merchant.name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchesSearch = !searchTerm || searchableText.includes(searchTerm);

      return matchesStatus && matchesSearch;
    });
  }

  renderFilterBar() {
    return (
      <div
        style={{
          display: "flex",
          gap: "16px",
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: "20px",
        }}
      >
        <TextField
          label="Search"
          value={this.state.search}
          onChange={this.onSearchChange}
          placeholder="Search by name, code, reference or comments"
          style={{ minWidth: "320px" }}
        />

        <TextField
          select
          label="Status"
          value={this.state.statusFilter}
          onChange={this.onStatusFilterChange}
          style={{ minWidth: "220px" }}
        >
          {this.getStatusOptions().map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </div>
    );
  }

  renderEmptyState() {
    return (
      <div
        style={{
          backgroundColor: "#fff",
          border: "1px solid #d7d7d7",
          padding: "16px 18px",
          color: "#555",
        }}
      >
        {this.props.emptyStateMessage}
      </div>
    );
  }

  render() {
    const filteredEvents = this.getFilteredEvents();
    const source = this.props.mode === "review_queue" ? "review_queue" : "all";
    const ticketEventColumns = buildTicketEventColumns;
    const tableRows = filteredEvents.map((ticketEvent) => ({
      ...ticketEvent,
      status: getDisplayStatus(ticketEvent.status, ticketEvent.end_date),
    }));

    return (
      <BOGraphQLLoader data={this.props.data}>
        <section className="padded">
          {this.renderFilterBar()}

          {filteredEvents.length === 0 ? (
            this.renderEmptyState()
          ) : (
            <ConfigurableTable
              name={this.props.name}
              history={this.props.history}
              pagination={this.props.pagination}
              columnConfigs={ticketEventColumns as any}
              defaultColumns={[
                "Name",
                "Status",
                "External reference",
                "Code",
                "Start date",
                "End date",
                "Location",
              ]}
              rows={tableRows}
              rowToLink={(ticketEvent: TicketEvent) =>
                `/snapscan/events/${ticketEvent.id}/summary?from=${source}`
              }
            />
          )}
        </section>
      </BOGraphQLLoader>
    );
  }
}

const ticketEventsQuery = gql`
  query TicketEvents {
    ticket_events {
      id
      status
      external_reference
      code
      colour
      name
      description
      start_date
      end_date
      location
      facebook_page
      instagram_page
      approval_comments
      merchant {
        id
        name
      }
    }
  }
`;

const withTicketEventsData = graphql<GqlData, Props>(ticketEventsQuery, {
  options: () => ({
    fetchPolicy: "network-only",
  }),
});

export default withPagination(
  withRouter(withTicketEventsData(TicketEventsTableContainer))
);
